// Day-month-year date and 24-hour time entry, the same on every device. Native
// date/datetime-local inputs follow the browser's locale (MM/DD/YYYY on a US-locale
// machine), so a permit window could be misread; these fields always read DD/MM/YYYY
// and HH:MM, Asia/Muscat time.
//
// The value given and returned is the canonical one the app already uses
// ('YYYY-MM-DDTHH:mm' for a date and time, 'YYYY-MM-DD' for a date; '' while incomplete
// or invalid), so lib/format.ts toLocalInput / fromLocalInput / inputTimeArg are unchanged.
// What the person types is kept as typed until it is complete; a clear message says
// what is wrong. Each box is at least 44px tall (gloves).
import { useEffect, useId, useRef, useState } from 'react'
import { TextInput } from '@/components/ui'
import { dmyDateEntry, joinDmyDateTime, parse24hTime, parseDmyDate, toDmyText, toTimeText } from '@/lib/format'

type Common = {
  /** id of the date box (the Field label points to it). */
  id?: string
  disabled?: boolean
  /** Extra text for screen readers, for example the field's label. */
  name?: string
}

function ErrorLine({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p className="mt-1 text-[13px] leading-normal font-semibold text-bad" role="alert">
      <span aria-hidden>! </span>
      {text}
    </p>
  )
}

/** Date (DD/MM/YYYY) and time (24-hour HH:MM) in Muscat time. */
export function DateTimeInput({ id, value, onChange, disabled, name }: Common & { value: string; onChange: (v: string) => void }) {
  const [dateText, setDateText] = useState(() => toDmyText(value))
  const [timeText, setTimeText] = useState(() => toTimeText(value))
  const [touched, setTouched] = useState(false)
  const emitted = useRef(value)

  // A value set from outside (a default, "use the time now", a reload) replaces what is shown.
  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value
      setDateText(toDmyText(value))
      setTimeText(toTimeText(value))
      setTouched(false)
    }
  }, [value])

  const entry = joinDmyDateTime(dateText, timeText)
  const send = (d: string, t: string) => {
    const next = joinDmyDateTime(d, t).value
    if (next !== emitted.current) {
      emitted.current = next
      onChange(next)
    }
  }
  const tidy = () => {
    setTouched(true)
    const d = parseDmyDate(dateText)
    const t = parse24hTime(timeText)
    if (d) setDateText(toDmyText(d))
    if (t) setTimeText(t)
  }
  const label = name ? `${name}: ` : ''
  const hintId = useId()
  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-2">
        {/* The date box is named by the field's own label (htmlFor = id); the format is its description. */}
        <TextInput
          id={id}
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/YYYY"
          maxLength={10}
          aria-label={id ? undefined : `${label}date, day/month/year`}
          aria-describedby={hintId}
          aria-invalid={touched && entry.error !== null ? true : undefined}
          value={dateText}
          disabled={disabled}
          onChange={(e) => {
            setDateText(e.target.value)
            send(e.target.value, timeText)
          }}
          onBlur={tidy}
        />
        <TextInput
          inputMode="numeric"
          autoComplete="off"
          placeholder="HH:MM"
          maxLength={5}
          aria-label={`${label}time, 24-hour`}
          aria-invalid={touched && entry.error !== null ? true : undefined}
          value={timeText}
          disabled={disabled}
          onChange={(e) => {
            setTimeText(e.target.value)
            send(dateText, e.target.value)
          }}
          onBlur={tidy}
        />
      </div>
      <p id={hintId} className="mt-1 text-xs text-ink-soft">
        Day/month/year and 24-hour time, Muscat time (GST, UTC+4).
      </p>
      <ErrorLine text={touched ? entry.error : null} />
    </div>
  )
}

/** Date only (DD/MM/YYYY). */
export function DateInput({ id, value, onChange, disabled, name }: Common & { value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(() => toDmyText(value))
  const [touched, setTouched] = useState(false)
  const emitted = useRef(value)
  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value
      setText(toDmyText(value))
      setTouched(false)
    }
  }, [value])
  const entry = dmyDateEntry(text)
  return (
    <div>
      <TextInput
        id={id}
        inputMode="numeric"
        autoComplete="off"
        placeholder="DD/MM/YYYY"
        maxLength={10}
        aria-label={id ? undefined : `${name ? `${name}: ` : ''}date, day/month/year`}
        aria-invalid={touched && entry.error !== null ? true : undefined}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value)
          const next = dmyDateEntry(e.target.value).value
          if (next !== emitted.current) {
            emitted.current = next
            onChange(next)
          }
        }}
        onBlur={() => {
          setTouched(true)
          const d = parseDmyDate(text)
          if (d) setText(toDmyText(d))
        }}
      />
      <ErrorLine text={touched ? entry.error : null} />
    </div>
  )
}
